import { OperatorModel, OperatorProperty } from '@/models/Operator';
import { GroupModel } from '@/models/Group';

abstract class BaseGroupingStrategy {
    protected targetOperators!: OperatorModel[];
    protected defaultOperatorNum!: number;

    protected createInitialGroups(groupNum: number): GroupModel[] {
        const initialGroups: GroupModel[] = [];
        for (let i = 0; i < groupNum; i++) {
            const initialGroup: GroupModel = {
                id: i + 1,
                operators: [],
                drawOdds: 100 / groupNum,
                drawable: true,
            };
            initialGroups.push(initialGroup);
        }
        return initialGroups;
    }

    protected calcOperatorNums(): number[] {
        const modNum: number = this.targetOperators.length % this.defaultOperatorNum;
        const groupNum: number = (this.targetOperators.length - modNum) / this.defaultOperatorNum;
        const operatorNums: number[] = [];
        if ( groupNum > modNum ) {
            for (let i = 0; i < groupNum - modNum; i++) {
                operatorNums.push(this.defaultOperatorNum);
            }
            for (let i = 0; i < modNum; i++) {
                operatorNums.push(this.defaultOperatorNum + 1);
            }
        } else {
            for (let i = 0; i < groupNum; i++) {
                operatorNums.push(this.defaultOperatorNum);
            }
            operatorNums.push(modNum);
        }
        return operatorNums;
    }

    public abstract grouping(): GroupModel[];

    public constructor(targetOperators: OperatorModel[], defaultOperatorNum: number) {
        this.targetOperators = targetOperators;
        this.defaultOperatorNum = defaultOperatorNum;
    }
}

class RandomGroupingStrategy extends BaseGroupingStrategy {
    public grouping(): GroupModel[] {
        const operatorNums = this.calcOperatorNums();
        const groups: GroupModel[] = this.createInitialGroups(operatorNums.length);
        const forGroupingOperators = JSON.parse(JSON.stringify(this.targetOperators));
        for (const targetOperator of forGroupingOperators) {
            const availableGroups = groups.filter((group, i) => group.operators.length < operatorNums[i]);
            const targetIndex = Math.floor( Math.random() * availableGroups.length );
            availableGroups[targetIndex].operators.push(targetOperator);
        }
        return groups;
    }
}

class BalancedGroupingStrategy extends BaseGroupingStrategy {
    protected targetProperties!: OperatorProperty[];

    public constructor(targetOperators: OperatorModel[], defaultOperatorNum: number,
                       targetProperties: OperatorProperty[]) {
        super(targetOperators, defaultOperatorNum);
        this.targetProperties = targetProperties;
    }

    public grouping(): GroupModel[] {
        const operatorNums = this.calcOperatorNums();
        const groups = this.createInitialGroups(operatorNums.length);
        const forGroupingOperators = JSON.parse(JSON.stringify(this.targetOperators));
        for (const targetOperator of forGroupingOperators) {
            const filterProporties: OperatorProperty[] =  JSON.parse(JSON.stringify(this.targetProperties));
            let availableGroups: GroupModel[];
            do {
                availableGroups = groups.filter((group, i) => {
                    if (group.operators.length >= operatorNums[i]) {
                        return false;
                    }
                    for (const property of filterProporties) {
                        const filteredOperators = group.operators.filter((operator) => {
                            return operator[property] === targetOperator[property];
                        });
                        if (filteredOperators.length > 0) {
                            return false;
                        }
                    }
                    return true;
                });
                filterProporties.splice(filterProporties.length - 1, 1);
            } while (availableGroups.length === 0);

            const targetIndex = Math.floor( Math.random() * availableGroups.length );
            availableGroups[targetIndex].operators.push(targetOperator);
        }
        return groups;
    }
}

export { BaseGroupingStrategy, RandomGroupingStrategy, BalancedGroupingStrategy };
